import {AfterViewInit, Component, inject, OnInit} from '@angular/core';
import {FooterComponent} from "../../../shared/footer/footer.component";
import {LoginPopupModalComponent} from "../../../modals/login-popup-modal/login-popup-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {ActivatedRoute, Router} from "@angular/router";
import {PATH} from "../../../../assets/constants";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {ButtonSize, ButtonState, ButtonType} from "../../../../assets/types/dtoTypes";
import {ButtonComponent} from "../../../shared/button/button.component";
import {MottoCardComponent} from "./motto-card/motto-card.component";
import {IntroCardComponent} from "./intro-card/intro-card.component";
import {IndustriesCardComponent} from "./industries-card/industries-card.component";
import {ShowPopupService} from "../../../shared/services/show-popup.service";
import {EmployerPartnerModalComponent} from "../../../modals/employer-partner-modal/employer-partner-modal.component";
import {LandingStatsComponent} from "./landing/landing-stats.component";
import {LandingPartnersComponent} from "./landing/landing-partners.component";
import {LandingTestimonialsComponent} from "./landing/landing-testimonials.component";
import {LandingNewsComponent} from "./landing/landing-news.component";
import {LandingSocialComponent} from "./landing/landing-social.component";
import {LandingFaqComponent} from "./landing/landing-faq.component";
import {LandingTeamComponent} from "./landing/landing-team.component";

declare const gsap: any;
declare const ScrollTrigger: any;

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  imports: [
    FooterComponent,
    TranslateModule,
    ButtonComponent,
    MottoCardComponent,
    IndustriesCardComponent,
    LandingPartnersComponent,
    LandingTestimonialsComponent,
    LandingNewsComponent,
    LandingSocialComponent,
    LandingFaqComponent,
    LandingTeamComponent,
  ],
  standalone: true
})
export class LoginPageComponent implements AfterViewInit, OnInit {
  private isSliderDragging = false;
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly popupService = inject(ShowPopupService);
  xzBikeTransform = 'translateX(0px) scaleX(1)';
  private xzBikeCurrentX = 0;
  private xzBikeTargetX = 0;
  private xzBikeScaleX = 1;
  private xzBikeRaf: number | null = null;
  private readonly XZ_BIKE_SMOOTHING = 0.12;
  currentLang: string;

  ngAfterViewInit(): void {
    this.initScroll();
    this.initializeSlider();
    this.initNavbar();
    this.initTimelineObserver();
    this.xzInitImageFlow();

    this.translate.get('login_page.jobs_tags.warehouse_worker').subscribe(() => {
      this.initInfiniteScroller();
    });

    window.addEventListener('resize', () => {
      this.initScroll();
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const error = params['error'];
      if (!error) {
        return;
      }

      const socialAccountErrorKey = this.resolveSocialAccountErrorTranslationKey(error);
      if (socialAccountErrorKey) {
        this.popupService.showPopup({
          status: 'failure',
          translationKey: socialAccountErrorKey
        });
      } else if (error === 'oauth-flow-invalid' || error === 'google-email-missing') {
        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.generalError'
        });
      } else if (error === 'email-already-registered' || error === 'user-already-exists') {
        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.emailAlreadyRegistered'
        });
      }

      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true
      });
      localStorage.removeItem('socialRegistrationProvider');
      localStorage.removeItem('isGoogleRegistration');
    });
  }


  changeLanguage( lang: string ) {
    this.translate.use( lang );
    this.currentLang = lang;
    localStorage.setItem( 'app_language', lang );
  }



  constructor() {
    this.currentLang = this.translate.currentLang || this.translate.getDefaultLang();

  }

  private resolveSocialAccountErrorTranslationKey(error: string): string | null {
    if (error === 'account-exists-with-google') {
      return 'job_details.popup.application.accountExistsWithGoogle';
    }
    if (error === 'account-exists-with-apple') {
      return 'job_details.popup.application.accountExistsWithApple';
    }
    if (error === 'account-exists-with-password') {
      return 'job_details.popup.application.accountExistsWithPassword';
    }
    return null;
  }

  // --- CORE GSAP SCROLL LOGIC ---
  private initScroll(): void {
    if (!gsap || !ScrollTrigger) return;

    ScrollTrigger.getAll().forEach((t: any) => t.kill());

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    gsap.utils.toArray<HTMLElement>('h1, p, .glass-card, .cta-button, h3').forEach((el) => {
      if (el.closest('.role-card')) return;
      // New landing trust/marketing sections manage their own layout — keep the
      // scroll-reveal away from them so headings/paragraphs aren't left mid-transform.
      if (el.closest('[class*="landing-"]')) return;
      const speed = el.closest('#section-about') && el.tagName === 'H1' ? 0.8 : 0.4;
      const movement = speed * 60;

      gsap.fromTo(
        el,
        {
          y: movement,
        },
        {
          y: 0,
          opacity: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom-=100',
            end: 'bottom top',
            scrub: 0.5,
          },
        }
      );
    });

    ScrollTrigger.refresh();
  }

  // --- SLIDER LOGIC ---
  private initializeSlider(): void {
    const sliderHandle = document.querySelector<HTMLElement>('#sliderHandle');
    const sliderLine = document.querySelector<HTMLElement>('.slider-line');
    const beforeImage = document.querySelector<HTMLElement>('.before-image');
    const afterImage = document.querySelector<HTMLElement>('.after-image');
    const sliderContainer = document.querySelector<HTMLElement>('.slider-container');

    if (!sliderHandle || !sliderLine || !beforeImage || !afterImage || !sliderContainer) return;

    const setInitialPosition = () => {
      sliderHandle.style.transition = 'left 0.3s ease';
      sliderLine.style.transition = 'left 0.3s ease';

      sliderHandle.style.left = '50%';
      sliderLine.style.left = '50%';

      beforeImage.style.clipPath = 'inset(0 50% 0 0)';
      afterImage.style.clipPath = 'inset(0 0 0 50%)';
    };

    const moveSlider = (clientX: number) => {
      const rect = sliderContainer.getBoundingClientRect();
      let offsetX = clientX - rect.left;

      if (offsetX < 0) offsetX = 0;
      if (offsetX > rect.width) offsetX = rect.width;

      const percentage = Math.round((offsetX / rect.width) * 100);

      sliderHandle.style.left = `${percentage}%`;
      sliderLine.style.left = `${percentage}%`;

      beforeImage.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
      afterImage.style.clipPath = `inset(0 0 0 ${percentage}%)`;
    };

    const startDragging = () => {
      this.isSliderDragging = true;
      sliderHandle.style.transition = 'none';
      sliderLine.style.transition = 'none';
    };

    const stopDragging = () => {
      this.isSliderDragging = false;
      sliderHandle.style.transition = 'left 0.3s ease';
      sliderLine.style.transition = 'left 0.3s ease';
    };

    // Mouse
    sliderHandle.addEventListener('mousedown', startDragging);
    sliderLine.addEventListener('mousedown', startDragging);

    window.addEventListener('mousemove', (event: MouseEvent) => {
      if (this.isSliderDragging) {
        moveSlider(event.clientX);
      }
    });

    window.addEventListener('mouseup', stopDragging);

    // Touch
    sliderHandle.addEventListener('touchstart', () => startDragging(), {passive: true});
    sliderLine.addEventListener('touchstart', () => startDragging(), {passive: true});

    window.addEventListener(
      'touchmove',
      (event: TouchEvent) => {
        if (this.isSliderDragging) {
          moveSlider(event.touches[0].clientX);
        }
      },
      {passive: true}
    );

    window.addEventListener('touchend', () => stopDragging(), {passive: true});

    setInitialPosition();
  }

  // --- NAVBAR LOGIC ---
  private initNavbar(): void {
    const nav = document.querySelector('nav');
    const effectEl = document.querySelector<HTMLElement>('.effect.filter');
    const textEl = document.querySelector<HTMLElement>('.effect.text');

    if (!nav || !effectEl || !textEl) return;

    const animationTime = 600;
    const pCount = 15;
    const colors = [1, 2, 3, 1, 2, 3, 1, 4];
    const timeVariance = 300;

    const noise = (n = 1) => n / 2 - Math.random() * n;

    const getXY = (distance: number, pointIndex: number, totalPoints: number) => {
      const x = distance * Math.cos(((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180));
      const y = distance * Math.sin(((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180));
      return [x, y];
    };

    const createParticle = (i: number, t: number, d: number[], r: number) => {
      const rotate = noise(r / 10);
      const minDistance = d[0];
      const maxDistance = d[1];

      const color = colors[Math.floor(Math.random() * colors.length)];
      const [sx, sy] = getXY(minDistance, pCount - i, pCount);
      const [ex, ey] = getXY(maxDistance + noise(7), pCount - i, pCount);

      return {
        start: [sx, sy],
        end: [ex, ey],
        time: t,
        scale: 1 + noise(0.2),
        color,
        rotate: rotate > 0 ? (rotate + r / 20) * 10 : (rotate - r / 20) * 10,
      };
    };

    const makeParticles = ($el: HTMLElement) => {
      const d = [90, 10];
      const r = 100;
      const bubbleTime = animationTime * 2 + timeVariance;

      $el.style.setProperty('--time', `${bubbleTime}ms`);

      for (let i = 0; i < pCount; i++) {
        const t = animationTime * 2 + noise(timeVariance * 2);
        const p = createParticle(i, t, d, r);

        $el.classList.remove('active');

        setTimeout(() => {
          const particle = document.createElement('span');
          const point = document.createElement('span');
          particle.classList.add('particle');
          particle.style.cssText = `
            --start-x: ${p.start[0]}px;
            --start-y: ${p.start[1]}px;
            --end-x: ${p.end[0]}px;
            --end-y: ${p.end[1]}px;
            --time: ${p.time}ms;
            --scale: ${p.scale};
            --color: var(--color-${p.color}, white);
            --rotate: ${p.rotate}deg;
          `;
          point.classList.add('point');
          particle.append(point);
          $el.append(particle);

          requestAnimationFrame(() => $el.classList.add('active'));

          setTimeout(() => {
            try {
              $el.removeChild(particle);
            } catch (e) {
              // ignore
            }
          }, t);
        }, 30);
      }
    };

    const updateEffectPosition = (element: HTMLElement) => {
      const pos = element.getBoundingClientRect();
      const styles = {
        left: `${pos.x}px`,
        top: `${pos.y}px`,
        width: `${pos.width}px`,
        height: `${pos.height}px`,
      };

      Object.assign(effectEl.style, styles);
      Object.assign(textEl.style, styles);
      textEl.classList.remove('hidden');
      textEl.innerText = element.innerText.trim();
    };

    const activate = ($el: HTMLElement) => {
      updateEffectPosition($el);

      if (!$el.classList.contains('active')) {
        nav.querySelectorAll('li').forEach((li) => li.classList.remove('active'));
        effectEl.querySelectorAll('.particle').forEach((p) => p.remove());

        $el.classList.add('active');
        textEl.classList.remove('active');

        setTimeout(() => textEl.classList.add('active'), 100);
        makeParticles(effectEl);
      }
    };

    nav.querySelectorAll<HTMLLIElement>('li').forEach(($el) => {
      const link = $el.querySelector('a');

      $el.addEventListener('click', () => activate($el));

      if (link) {
        link.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate($el);
          }
        });
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      const activeEl = nav.querySelector<HTMLElement>('li.active');
      if (activeEl) updateEffectPosition(activeEl);
    });

    resizeObserver.observe(document.body);

    setTimeout(() => {
      const firstItem = nav.querySelector<HTMLElement>('li');
      if (firstItem) activate(firstItem);
    }, 200);
  }

  // Timeline intersection animations
  private initTimelineObserver(): void {
    const items = document.querySelectorAll<HTMLElement>('.timeline-item');
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('show');
          } else {
            entry.target.classList.remove('show');
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    items.forEach((item) => observer.observe(item));
  }

  // Infinite horizontal scroll clones
  private initInfiniteScroller(): void {
    const scrollers = document.querySelectorAll<HTMLElement>('.scroller');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    scrollers.forEach((scroller) => {
      scroller.setAttribute('data-animated', 'true');

      const scrollerInner = scroller.querySelector<HTMLElement>('.scroller__inner');
      if (!scrollerInner) return;

      const scrollerContent = Array.from(scrollerInner.children);
      scrollerContent.forEach((item) => {
        const duplicatedItem = item.cloneNode(true) as HTMLElement;
        duplicatedItem.setAttribute('aria-hidden', 'true');
        scrollerInner.appendChild(duplicatedItem);
      });
    });
  }

  // Panoramic background flow at bottom of page
  private xzInitImageFlow(): void {
    const flowWrapper = document.getElementById('xz_Flow_Main_Wrapper');
    if (!flowWrapper) return;

    let totalImagePixelWidth = 0;
    let maxOffset = 0;
    let targetPos = 0;
    let currentPos = 0;
    const SMOOTHING = 0.07;
    const SCROLL_SPEED = 1.5;
    let animationId: number | null = null;

    const calculateImageMetrics = (callback: () => void) => {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        totalImagePixelWidth = window.innerHeight * aspectRatio;
        callback();
      };
      img.src = '../assets/web-scroll.png';
    };

    const updateBackgroundPosition = () => {
      (flowWrapper as HTMLElement).style.backgroundPositionX = `${-currentPos}px`;
    };

    const updateMetrics = () => {
      maxOffset = totalImagePixelWidth - window.innerWidth;
      targetPos = Math.max(0, Math.min(maxOffset, targetPos));
      currentPos = targetPos;
      updateBackgroundPosition();
    };

    const animateFlow = () => {
      const diff = targetPos - currentPos;
      if (Math.abs(diff) < 0.1) {
        currentPos = targetPos;
        updateBackgroundPosition();
        animationId = null;
        return;
      }

      currentPos += diff * SMOOTHING;
      updateBackgroundPosition();
      animationId = requestAnimationFrame(animateFlow);
    };


    const handleWheelInput = (e: WheelEvent) => {
      const scrollDelta = e.deltaY;
      const scrollingDown = scrollDelta > 0;
      const scrollingUp = scrollDelta < 0;
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
      const canHorizontalScroll = maxOffset > 0;
      const isAtHorizontalStart = targetPos <= 0.5;
      const isAtHorizontalEnd = targetPos >= maxOffset - 0.5;

      // If we're not in the special bottom/horizontal case, let the browser do normal scrolling.
      if (!isAtBottom || !canHorizontalScroll) {
        return;
      }

      // From here on, we *want* to hijack the scroll -> only now try preventDefault
      if (!e.cancelable) {
        // If it's not cancelable, don't even try
        return;
      }

      e.preventDefault();

      if (scrollingDown) {
        if (isAtHorizontalEnd) return;
        targetPos = Math.min(maxOffset, targetPos + scrollDelta * SCROLL_SPEED);
        if (animationId === null) {
          animationId = requestAnimationFrame(animateFlow);
        }
        return;
      }

      if (scrollingUp) {
        if (isAtHorizontalStart) return;
        targetPos = Math.max(0, targetPos + scrollDelta * SCROLL_SPEED);
        if (animationId === null) {
          animationId = requestAnimationFrame(animateFlow);
        }
      }
    };

    const handleTouchControl = () => {
      let touchMoveX = 0;
      const sensitivity = 1;
      let isTouchingHorizontalArea = false;

      document.addEventListener(
        'touchstart',
        (e) => {
          const isAtBottom =
            window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
          if (isAtBottom) {
            isTouchingHorizontalArea = true;
            touchMoveX = e.touches[0].screenX;
            if (animationId !== null) cancelAnimationFrame(animationId);
          } else {
            isTouchingHorizontalArea = false;
          }
        },
        {passive: true}
      );

      document.addEventListener(
        'touchmove',
        (e) => {
          if (isTouchingHorizontalArea) {
            const deltaX = e.touches[0].screenX - touchMoveX;
            touchMoveX = e.touches[0].screenX;
            targetPos = Math.max(0, Math.min(maxOffset, targetPos - deltaX * sensitivity));
            currentPos = targetPos;
            updateBackgroundPosition();
          }
        },
        {passive: true}
      );

      document.addEventListener(
        'touchend',
        () => {
          if (isTouchingHorizontalArea) {
            animationId = requestAnimationFrame(animateFlow);
          }
          isTouchingHorizontalArea = false;
        },
        {passive: true}
      );
    };

    const handleWindowResize = () => {
      const oldMaxOffset = maxOffset;
      calculateImageMetrics(() => {
        updateMetrics();
        if (oldMaxOffset > 0 && maxOffset > 0) {
          const ratio = targetPos / oldMaxOffset;
          targetPos = maxOffset * ratio;
        }
        currentPos = targetPos;
        updateBackgroundPosition();
      });
    };

    calculateImageMetrics(() => {
      updateMetrics();
      window.addEventListener('wheel', handleWheelInput, {passive: false});
      window.addEventListener('resize', handleWindowResize);
      handleTouchControl();
    });
  }

  openLoginModal() {
    this.dialog.open(LoginPopupModalComponent, {width: 'auto', height: 'auto', disableClose: false,});
  }

  openEmployerModal() {
    this.dialog.open(EmployerPartnerModalComponent, {width: 'auto', maxWidth: 'auto', disableClose: false});
  }

  openRegistrationModal() {
    this.router.navigate([PATH.REGISTER]);
  }

  xzOnBikeMove(event: MouseEvent, zone: HTMLElement): void {
    const rect = zone.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));

    const bikeWidth = 90;
    const padding = 10;
    const maxX = Math.max(0, rect.width - bikeWidth - padding * 2);

    const nextTarget = Math.round(padding + percent * maxX);

    const delta = nextTarget - this.xzBikeTargetX;
    if (Math.abs(delta) > 0.5) {
      this.xzBikeScaleX = delta >= 0 ? 1 : -1;
    }

    this.xzBikeTargetX = nextTarget;

    if (this.xzBikeRaf === null) {
      this.xzBikeRaf = requestAnimationFrame(() => this.xzBikeAnimate());
    }
  }

  private xzBikeAnimate(): void {
    const diff = this.xzBikeTargetX - this.xzBikeCurrentX;

    if (Math.abs(diff) < 0.2) {
      this.xzBikeCurrentX = this.xzBikeTargetX;
      this.xzBikeTransform = `translateX(${this.xzBikeCurrentX}px) scaleX(${this.xzBikeScaleX})`;
      this.xzBikeRaf = null;
      return;
    }

    this.xzBikeCurrentX += diff * this.XZ_BIKE_SMOOTHING;

    this.xzBikeTransform = `translateX(${this.xzBikeCurrentX}px) scaleX(${this.xzBikeScaleX})`;
    this.xzBikeRaf = requestAnimationFrame(() => this.xzBikeAnimate());
  }

  xzOnBikeLeave(): void {
    this.xzBikeTargetX = 0;
    this.xzBikeScaleX = 1;

    if (this.xzBikeRaf === null) {
      this.xzBikeRaf = requestAnimationFrame(() => this.xzBikeAnimate());
    }
  }

  contactUs(): void {
    window.location.href = 'mailto:info@bipperz.com';
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
}
